/**
 * Validation error viewer (P2-16, MUI port).
 *
 * The exact cell/row list AND the aggregate total beside it — a truncated
 * list must never read as a complete one. Renders the A-13 template diff
 * when present. No "run anyway" affordance, deliberately.
 */
import { useTranslation } from "react-i18next";
import {
  Alert,
  Card,
  CardContent,
  Chip,
  Stack,
  TableCell,
  TableRow,
  Typography,
} from "@mui/material";
import type { DriftRow, ValidationReport as Report } from "../lib/api";
import { DataTable } from "./ui/DataTable";
import { SectionLabel } from "./ui/SectionLabel";

const DIFF_COLOR: Record<DriftRow["status"], "success" | "warning" | "error"> = {
  ok: "success",
  moved: "warning",
  renamed: "error",
  missing: "error",
  extra: "warning",
};

export function ValidationReportView({
  report,
  errors,
  diff,
}: {
  report?: Report;
  errors?: string[];
  diff?: DriftRow[];
}) {
  if (diff && diff.length > 0) return <TemplateDiff diff={diff} />;
  if (report) return <CellErrors report={report} />;
  if (errors && errors.length > 0)
    return (
      <Alert severity="error">
        {errors.map((line) => (
          <Typography key={line} variant="caption" className="num" display="block">
            {line}
          </Typography>
        ))}
      </Alert>
    );
  return null;
}

function CellErrors({ report }: { report: Report }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="baseline">
          <SectionLabel sx={{ color: "error.main" }}>
            {t("validation.title")}
          </SectionLabel>
          <Typography variant="body2" fontWeight={700} color="error" className="num">
            {t("validation.errors", { count: report.total_errors })}
          </Typography>
        </Stack>
        {report.truncated > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {report.issues.length} / {report.total_errors.toLocaleString()} ·{" "}
            {report.truncated.toLocaleString()} +
          </Typography>
        )}
        <DataTable
          maxHeight={360}
          columns={[
            { key: "row", label: t("validation.row") },
            { key: "column", label: t("validation.column") },
            { key: "code", label: t("validation.code") },
            { key: "value", label: t("validation.value") },
            { key: "detail", label: t("validation.detail") },
          ]}
        >
          {report.issues.map((issue, i) => (
            <TableRow key={`${issue.row ?? "?"}-${issue.column ?? "?"}-${issue.code}-${i}`}>
              <TableCell className="num">{issue.row ?? "—"}</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>{issue.column ?? "—"}</TableCell>
              <TableCell>
                <Chip size="small" color="error" variant="outlined" label={issue.code} />
              </TableCell>
              <TableCell className="num">{issue.raw_value ?? "—"}</TableCell>
              <TableCell>{issue.message}</TableCell>
            </TableRow>
          ))}
        </DataTable>
        {report.column_stats.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
            {report.column_stats
              .map((s) =>
                t("validation.columnStat", {
                  column: s.column,
                  invalid: s.invalid_format,
                  empty: s.empty_cells,
                }),
              )
              .join(" · ")}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

function TemplateDiff({ diff }: { diff: DriftRow[] }) {
  const { t } = useTranslation();
  return (
    <Card>
      <CardContent>
        <SectionLabel sx={{ color: "error.main" }}>
          {t("validation.driftTitle")}
        </SectionLabel>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {t("validation.driftBody")}
        </Typography>
        <DataTable
          maxHeight={320}
          columns={[
            { key: "expected", label: t("validation.expected") },
            { key: "expectedIndex", label: t("validation.index") },
            { key: "found", label: t("validation.found") },
            { key: "foundIndex", label: t("validation.index") },
            { key: "status", label: t("validation.status") },
          ]}
        >
          {diff.map((row) => (
            <TableRow key={`${row.column}-${row.status}-${row.found ?? ""}`}>
              <TableCell sx={{ fontWeight: 600 }}>{row.column}</TableCell>
              <TableCell className="num">{row.expected_index ?? "—"}</TableCell>
              <TableCell className="num">{row.found ?? "—"}</TableCell>
              <TableCell className="num">{row.found_index ?? "—"}</TableCell>
              <TableCell>
                <Chip
                  size="small"
                  color={DIFF_COLOR[row.status]}
                  label={t(`validation.statuses.${row.status}`, { defaultValue: row.status })}
                />
              </TableCell>
            </TableRow>
          ))}
        </DataTable>
      </CardContent>
    </Card>
  );
}
